import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { ASSOCIATION_CONFIG } from "./config/associationConfig";
import { isEqual, isNil } from "lodash";
import SelectedAssociations from "./components/display/SelectedAssociations";
import Flx from "../layout/Flx";
import { Button } from "@mui/material";
import AssociationSelector from "./components/selectors/AssociationSelector";
import { useAssociationState } from "./hooks/useAssociationState";
import { removeEmptyArrays } from "../../_src_shared/utils/objects/removeEmptyArrays";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";

/**
 * Generic Associations Selector Component
 */
const AssociationsSelector = ({
  associationTypeName,
  associationTypeId,
  associationToDeleteName,
  associations = {},
  enabledAssociations = null,
  onSave,
}) => {
  const loan = useSelector((state) => state.loanDrilldown);
  const { addAssociation, removeAssociation, loading } = useUnderwritingHook();
  const dispatch = useDispatch();
  // Memoize keys to prevent recreation
  const allAssociationKeys = useMemo(() => Object.keys(ASSOCIATION_CONFIG), []);
  const activeAssociationKeys = useMemo(() => {
    if (enabledAssociations && Array.isArray(enabledAssociations)) {
      return enabledAssociations;
    }
    return allAssociationKeys;
  }, [enabledAssociations, allAssociationKeys]);
  const [associationIds, setAssociationIds] = useAssociationState(
    associations,
    activeAssociationKeys
  );


  const isEditing = useMemo(() => {
    // console.log("isEditing:", isEqual(associationIds, associations), {
    //   associationIds,
    //   associations,
    // });

    const cleanedAssociationIds = removeEmptyArrays(associationIds);

    if (isEqual(associationIds, associations)) {
      return false;
    }
    if (isEqual(cleanedAssociationIds, associations)) {
      return false;
    }

    return true;
  }, [associationIds, associations]);
  // Use custom hook with deep comparison

  const addAttachmentIdByKey = useCallback((newId, name) => {
    if (isNil(newId) || isNil(name)) {
      console.warn("addAttachmentIdByKey: newId or name is null/undefined");
      return;
    }
    setAssociationIds((prev) => ({
      ...prev,
      [name]: [...(prev[name] || []), newId],
    }));
  }, []);

  const deleteAttachmentIdByKey = useCallback((id, name) => {
    if (isNil(id) || isNil(name)) {
      console.warn("deleteAttachmentIdByKey: id or name is null/undefined");
      return;
    }
    setAssociationIds((prev) => ({
      ...prev,
      [name]: (prev[name] || []).filter((attachmentId) => attachmentId !== id),
    }));
  }, []);

  const resetAssociations = useCallback((id, name) => {
    setAssociationIds(associations);
  }, []);

  const getArrayDifferences = useCallback(
    (oldObj, newObj, returnWithType = false) => {
      // Handle null/undefined inputs
      if (oldObj === null || oldObj === undefined) {
        console.warn("getArrayDifferences: oldObj is null or undefined");
        return returnWithType ? { differences: {}, type: "unchanged" } : {};
      }

      if (newObj === null || newObj === undefined) {
        console.warn("getArrayDifferences: newObj is null or undefined");
        return returnWithType ? { differences: {}, type: "unchanged" } : {};
      }

      // Handle non-object types
      if (typeof oldObj !== "object" || Array.isArray(oldObj)) {
        console.warn(
          "getArrayDifferences: oldObj must be an object, not",
          typeof oldObj
        );
        return returnWithType ? { differences: {}, type: "unchanged" } : {};
      }

      if (typeof newObj !== "object" || Array.isArray(newObj)) {
        console.warn(
          "getArrayDifferences: newObj must be an object, not",
          typeof newObj
        );
        return returnWithType ? { differences: {}, type: "unchanged" } : {};
      }

      try {
        const differences = {};
        const addedDetails = {};
        const removedDetails = {};
        let hasAdditions = false;
        let hasRemovals = false;

        // Get all unique keys from both objects with safe access
        let keysOld, keysNew;

        try {
          keysOld = Object.keys(oldObj);
        } catch (e) {
          keysOld = [];
        }

        try {
          keysNew = Object.keys(newObj);
        } catch (e) {
          keysNew = [];
        }

        const allKeys = new Set([...keysOld, ...keysNew]);

        for (const key of allKeys) {
          // Safely access properties
          let arrOld, arrNew;

          try {
            arrOld = oldObj[key];
          } catch (e) {
            arrOld = undefined;
          }

          try {
            arrNew = newObj[key];
          } catch (e) {
            arrNew = undefined;
          }

          // Skip if both are not arrays or don't exist
          if (!Array.isArray(arrOld) && !Array.isArray(arrNew)) {
            continue;
          }

          // Convert to arrays if undefined/null, with error handling
          const arrayOld = Array.isArray(arrOld) ? arrOld : [];
          const arrayNew = Array.isArray(arrNew) ? arrNew : [];

          try {
            // Items in new that are not in old (additions)
            const added = arrayNew.filter((item) => {
              try {
                return !arrayOld.includes(item);
              } catch (e) {
                return false;
              }
            });

            // Items in old that are not in new (removals)
            const removed = arrayOld.filter((item) => {
              try {
                return !arrayNew.includes(item);
              } catch (e) {
                return false;
              }
            });

            // Track additions
            if (added.length > 0) {
              hasAdditions = true;
              addedDetails[key] = added;
            }

            // Track removals
            if (removed.length > 0) {
              hasRemovals = true;
              removedDetails[key] = removed;
            }

            // Combine all differences for this key
            const allDiffs = [...added, ...removed];

            // Only include if there are actual changes
            if (allDiffs.length > 0) {
              differences[key] = allDiffs;
            }
          } catch (e) {
            console.warn(`Error filtering differences for key "${key}":`, e);
          }
        }

        // If not returning with type, just return differences
        if (!returnWithType) {
          return differences;
        }

        // Determine the type of change
        let type = "unchanged";
        if (hasAdditions && hasRemovals) {
          type = "both";
        } else if (hasAdditions) {
          type = "added";
        } else if (hasRemovals) {
          type = "removed";
        }

        // Build the return object based on what changed
        const result = {
          differences,
          type,
        };

        // Only include added/removed if they exist
        if (hasAdditions) {
          result.added = addedDetails;
        }

        if (hasRemovals) {
          result.removed = removedDetails;
        }

        return result;
      } catch (error) {
        console.error("getArrayDifferences: Unexpected error occurred", error);
        return returnWithType ? { differences: {}, type: "unchanged" } : {};
      }
    },
    []
  );

  //   if (isNil(associationTypeId)) {
  //     console.warn(
  //       "Cannot save associations: associationTypeId is null/undefined"
  //     );
  //     return;
  //   }

  //   if (onSave) {
  //     onSave(associationIds);
  //   } else {
  //     const diff = getArrayDifferences(associations, associationIds);
  //     const diffWithTyp = getArrayDifferences(
  //       associations,
  //       associationIds,
  //       true
  //     );
  //     console.log("Saving associations with differences:", {
  //       diff,
  //       diffWithTyp,
  //     });
  //     return;
  //     addAssociation({
  //       dealId: loan?._id,
  //       associationData: associationIds,
  //       associationTypeName,
  //       associationTypeId,
  //     });
  //   }
  // }, [
  //   associationIds,
  //   associationTypeName,
  //   associationTypeId,
  //   loan?._id,
  //   onSave,
  //   addAssociation,
  // ]);

  const handleSaveOrRemoveAssociations = useCallback(() => {
    if (isNil(associationTypeId)) {
      console.warn(
        "Cannot save associations: associationTypeId is null/undefined"
      );
      return;
    }

    if (onSave) {
      onSave(associationIds);
    } else {
      const diff = getArrayDifferences(associations, associationIds, true);

      if (diff?.type === "removed") {
        console.log("#### STARTING REMOVED PROCESS ####", diff?.removed);

        const removed = diff?.removed;
        const associationTypes = Object.keys(removed);

        // Calculate total number of removals
        let totalRemovals = 0;
        associationTypes.forEach((associationType) => {
          totalRemovals += removed[associationType].length;
        });

        let currentRemovalIndex = 0;

        associationTypes.forEach((associationType) => {
          removed[associationType].forEach((id) => {
            currentRemovalIndex++;
            const isLastIteration = currentRemovalIndex === totalRemovals;

            // console.log("Removing Request Params", {
            //   dealId: loan?._id,
            //   associationTypeName: associationType,
            //   associationTypeId: associationTypeId,
            //   associationIdToDelete: id,
            //   isLastIteration,
            //   onSuccessFn: (d) => {
            //     if (isLastIteration) {
            //       console.log("BOTH - Now Adding The new ones:", {
            //         dealId: loan?._id,
            //         associationData: associationIds,
            //         associationTypeName,
            //         associationTypeId,
            //         onSuccessFn: (d) => {
            //           dispatch(loanDrilldownSet(d));
            //         },
            //       });
            //       // addAssociation({
            //       //   dealId: loan?._id,
            //       //   associationData: associationIds,
            //       //   associationTypeName,
            //       //   associationTypeId,
            //       //   onSuccessFn: (d) => {
            //       //     dispatch(loanDrilldownSet(d));
            //       //   }
            //       // });
            //     }
            //   },
            // });
            return;
            // Uncomment when ready to actually call removeAssociation
            removeAssociation({
              dealId: loan?._id,
              associationTypeName: associationType,
              associationToDeleteName,
              associationTypeId: associationTypeId,
              associationIdToDelete: id,
              onSuccessFn: (d) => {
                if (isLastIteration) {
                  console.log("#### FINISH REMOVED --- RESET STATE ####");
                  dispatch(loanDrilldownSet(d));
                }
              },
            });
          });
        });
        return;
      }

      if (diff?.type === "added") {
        console.log("#### STARTING ADD PROCESS ####", diff?.added);
        addAssociation({
          dealId: loan?._id,
          // associationData: associationIds,
          associationData: diff?.added,
          associationTypeName,
          associationTypeId,
          onSuccessFn: (d) => {
            console.log("#### FINISH ADDED --- RESET STATE ####");
            // dispatch(loanDrilldownSet(d));
          },
        });
        return;
      }

      if (diff?.type === "both") {
        console.log("#### STARTING BOTH PROCESS ####", diff?.removed);
        // console.log("Both adding and removing the following:");
        // console.log("ADDING:", diff?.added);
        // console.log("REMOVING:", diff?.removed);

        // Handle removals first
        const removed = diff?.removed;
        if (removed) {
          const associationTypes = Object.keys(removed);

          // Calculate total number of removals
          let totalRemovals = 0;
          associationTypes.forEach((associationType) => {
            totalRemovals += removed[associationType].length;
          });

          let currentRemovalIndex = 0;
          console.log("Starting Removing -> ");
          associationTypes.forEach((associationType) => {
            removed[associationType].forEach((id) => {
              currentRemovalIndex++;
              const isLastIteration = currentRemovalIndex === totalRemovals;

              // console.log("BOTH - Removing Request Params", {
              //   dealId: loan?._id,
              //   associationTypeName: associationType,
              //   associationTypeId: associationTypeId,
              //   associationIdToDelete: id,
              //   onSuccessFn: (d) => {
              //     if (isLastIteration) {
              //       dispatch(loanDrilldownSet(d));
              //     }
              //   },
              // });

              removeAssociation({
                dealId: loan?._id,
                associationTypeName: associationType,
                associationTypeId: associationTypeId,
                associationIdToDelete: id,
                onSuccessFn: (d) => {
                  if (isLastIteration) {
                    console.log("Finished Removing -> Starting Add");
                    // dispatch(loanDrilldownSet(d));
                    addAssociation({
                      dealId: loan?._id,
                      // associationData: associationIds,
                      associationData: diff?.added,
                      associationTypeName,
                      associationTypeId,
                      onSuccessFn: (d) => {
                        console.log(
                          "#### FINISH BOTH PROCESS ####",
                          diff?.removed
                        );
                        // dispatch(loanDrilldownSet(d));
                      },
                    });
                  }
                },
                // onSuccessFn: (d) => {
                //   if (isLastIteration) {
                //     // dispatch(loanDrilldownSet(d));
                //     console.log("FINISH --- RESET STATE");
                //   }
                // },
              });
            });
          });
        }

        // Handle additions
        // const added = diff?.added;
        // if (added) {
        //   // Add your logic for handling additions here
        //   console.log("Processing additions:", added);
        // }

        return;
      }

      return;
      addAssociation({
        dealId: loan?._id,
        associationData: associationIds,
        associationTypeName,
        associationTypeId,
      });
    }
  }, [
    associationIds,
    associationTypeName,
    associationTypeId,
    associations,
    loan?._id,
    onSave,
    addAssociation,
    removeAssociation,
    dispatch,
    getArrayDifferences,
  ]);


  // console.log(
  //   "AssociationsSelector Render:",
  //   isEqual(cleanedAssociationIds, cleanedAssociations),
  //   { cleanedAssociationIds, cleanedAssociations }
  // );
  // Early return AFTER all hooks
  if (isNil(loan)) {
    console.warn("AssociationsSelector: loan data is not available");
    return null;
  }

  return (
    <Flx column gap={1}>
      <SelectedAssociations
        loan={loan}
        associationIds={associationIds}
        activeAssociationKeys={activeAssociationKeys}
        onDeleteAttachment={deleteAttachmentIdByKey}
      />
      <Flx ac gap={1} wrap sx={{ mt: 0.5 }}>
        <AssociationSelector
          loan={loan}
          associationIds={associationIds}
          activeAssociationKeys={activeAssociationKeys}
          onAdd={addAttachmentIdByKey}
        />

        {isEditing ? (
          <Button variant="outlined" onClick={resetAssociations}>
            Cancel
          </Button>
        ) : null}
        <Button
          loading={loading}
          disabled={!isEditing}
          // disabled={
          //   isEqual(cleanedAssociationIds, cleanedAssociations) ||
          //   !hasAnyAssociations ||
          //   isNil(associationTypeId)
          // }
          onClick={handleSaveOrRemoveAssociations}
        >
          Save Associations
          {/* Save Associations */}
        </Button>
      </Flx>
    </Flx>
  );
};

/**
 //returnWithType
getArrayDifferences(associations, associationIds, true);
{
  differences: {
    loanDocuments: ["68f0f6ad4e79a360968c1a92"],
    borrowerEntity: ["68f0f7e54e79a360968c1b87"],
  },
  type: "both", // 'added', 'removed', or 'both'
};

---------------------------------

//returnWithType - when removed
getArrayDifferences(associations, associationIds, true);
{
  differences: {
    loanDocuments: ["68f0f6ad4e79a360968c1a92"],
    borrowerEntity: ["68f0f7e54e79a360968c1b87"],
  },
  type: "both", // 'added', 'removed', or 'both'
  removed: {
    loanDocuments: ["78f004ad4e79a36092756a92"]
  }
};

---------------------------------

//returnWithType - when added
getArrayDifferences(associations, associationIds, true);
{
  differences: {
    loanDocuments: ["68f0f6ad4e79a360968c1a92"],
    borrowerEntity: ["68f0f7e54e79a360968c1b87"],
  },
  type: "added", // 'added', 'removed', or 'both'
  added: {
    borrowerEntity: ["68f0f6ad4e79a360968c1a92"]
  }
};

---------------------------------

//returnWithType - when both
getArrayDifferences(associations, associationIds, true);
{
  differences: {
    loanDocuments: ["68f0f6ad4e79a360968c1a92"],
    borrowerEntity: ["68f0f7e54e79a360968c1b87"],
  },
  type: "both", // 'added', 'removed', or 'both'
  added: {
    borrowerEntity: ["68f0f7e54e79a360968c1b87"]
  }
  removed: {
    loanDocuments: ["78f004ad4e79a36092756a92"]
  }
};


---------------------------------

//returnWithoutType

getArrayDifferences(associations, associationIds);
const  = {
  loanDocuments: ["68f0f6ad4e79a360968c1a92"],
  borrowerEntity: ["68f0f7e54e79a360968c1b87"],
};


 */
// const handleSaveAssociations = useCallback(() => {
export default AssociationsSelector;
